"use client";

import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PreviewPanel } from "@/components/configurator/preview-panel";
import { TextInputField } from "@/components/configurator/text-input-field";
import { FontPicker } from "@/components/configurator/font-picker";
import { ColorPicker } from "@/components/configurator/color-picker";
import { SizeControl } from "@/components/configurator/size-control";
import { SummaryCard } from "@/components/configurator/summary-card";
import { AddToCartButton } from "@/components/configurator/add-to-cart-button";
import { TestOrderButton } from "@/components/configurator/test-order-button";
import { usePhysicalSize } from "@/hooks/use-physical-size";
import { getFontOption, defaultFontId } from "@/lib/fonts";
import { getColorOption, defaultColorId } from "@/lib/colors";
import {
  DEFAULT_TEXT,
  DEFAULT_HEIGHT_CM,
  MIN_HEIGHT_CM,
  MAX_HEIGHT_CM,
  DEFAULT_DEPTH_MM,
  MIN_DEPTH_MM,
  MAX_DEPTH_MM,
} from "@/lib/config";

interface TextConfiguratorProps {
  /** Internal test button for the print pipeline - see TestOrderButton. */
  showTestButton?: boolean;
}

export function TextConfigurator({ showTestButton = false }: TextConfiguratorProps) {
  const [text, setText] = useState(DEFAULT_TEXT);
  const [fontId, setFontId] = useState(defaultFontId);
  const [colorId, setColorId] = useState(defaultColorId);
  const [heightCm, setHeightCm] = useState(DEFAULT_HEIGHT_CM);
  const [depthMm, setDepthMm] = useState(DEFAULT_DEPTH_MM);

  const font = getFontOption(fontId);
  const color = getColorOption(colorId);

  const { widthCm, heightCm: preciseHeightCm, fontsReady } = usePhysicalSize(
    text,
    font.family,
    font.weight,
    heightCm
  );

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
      <Card>
        <CardContent>
          <PreviewPanel
            text={text}
            font={font}
            color={color}
            widthCm={widthCm}
            heightCm={preciseHeightCm}
            depthCm={depthMm / 10}
          />
        </CardContent>
      </Card>

      <div className="space-y-5">
        <Card>
          <CardContent className="space-y-6">
            <TextInputField value={text} onChange={setText} />

            <Separator />

            <div className="space-y-2">
              <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                Schriftart
              </p>
              <FontPicker value={fontId} onChange={setFontId} />
            </div>

            <Separator />

            <div className="space-y-2">
              <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                Farbe
              </p>
              <ColorPicker value={colorId} onChange={setColorId} />
            </div>

            <Separator />

            <SizeControl
              label="Schriftgrösse (Texthöhe)"
              value={heightCm}
              onChange={setHeightCm}
              min={MIN_HEIGHT_CM}
              max={MAX_HEIGHT_CM}
              unit="cm"
            />

            <Separator />

            <SizeControl
              label="Schrift-Tiefe"
              value={depthMm}
              onChange={setDepthMm}
              min={MIN_DEPTH_MM}
              max={MAX_DEPTH_MM}
              unit="mm"
            />
          </CardContent>
        </Card>

        <SummaryCard
          text={text}
          font={font}
          color={color}
          widthCm={widthCm}
          heightCm={preciseHeightCm}
          depthMm={depthMm}
        />

        <AddToCartButton
          text={text}
          fontId={fontId}
          colorId={colorId}
          widthCm={widthCm}
          heightCm={preciseHeightCm}
          depthMm={depthMm}
          fontsReady={fontsReady}
        />

        {showTestButton && (
          <TestOrderButton
            text={text}
            fontId={fontId}
            colorId={colorId}
            widthCm={widthCm}
            heightCm={preciseHeightCm}
            depthMm={depthMm}
            fontsReady={fontsReady}
          />
        )}
      </div>
    </div>
  );
}
